import { useCallback, useEffect, useState } from "react";
import { endSession, loadIndex, renewSession, RENEW_MS } from "./store";
import { Login } from "./Login";
import { Timeline } from "./Timeline";
import type { Row } from "./types";

export default function App() {
  const [rows, setRows] = useState<Row[] | null>(null);
  // Locked from the first paint, whatever cookie the browser still holds: the
  // password is asked for on every visit, not once a month.
  const [locked, setLocked] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  // Drops the archive out of memory, not just out of view.
  const lock = useCallback(() => {
    setRows(null);
    setLocked(true);
  }, []);

  // There is no /api/me: the index request IS the auth check. A 401 means show
  // the lock, and success means we already hold the only payload we ever fetch.
  const load = useCallback(() => {
    setErr(null);
    loadIndex()
      .then((r) => {
        // Left while it loaded: do not open the archive behind the reader's back.
        if (document.visibilityState === "hidden") return endSession();
        setLocked(false);
        setRows(r);
      })
      .catch((e) => (String(e).includes("unauthorised") ? lock() : setErr(String(e))));
  }, [lock]);

  // Leaving the page locks it. `hidden` is switching tab or app, which on a
  // phone is how a page is left -- mobile Safari keeps a tab open for weeks.
  // pagehide is closing, reloading and navigating away, including into the
  // back/forward cache, which would otherwise restore the archive unlocked.
  useEffect(() => {
    const leave = () => { lock(); endSession(); };
    const onVisibility = () => { if (document.visibilityState === "hidden") leave(); };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", leave);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", leave);
    };
  }, [lock]);

  // The server's session lasts five minutes; only an open page renews it.
  useEffect(() => {
    if (locked) return;
    const id = setInterval(() => {
      renewSession().catch((e) => { if (String(e).includes("unauthorised")) lock(); });
    }, RENEW_MS);
    return () => clearInterval(id);
  }, [locked, lock]);

  if (err) return <p style={{ padding: 16 }}>{err}</p>;
  if (locked) return <Login onSuccess={load} />;
  if (!rows) return <p style={{ padding: 16 }}>loading…</p>;
  return <Timeline rows={rows} />;
}
